import i18n from "i18next";
import {Consts} from "./consts";
import {phenotypeMap, strengthUnits} from "../../resources/json/jsons";

export function getDateStr(date, withTime) {
  const minutes = withTime ? String(date?.getMinutes())?.padStart(2, "0") : '';
  const hours = withTime ? String(date?.getHours())?.padStart(2, "0") : '';
  const day = String(date?.getDate())?.padStart(2, "0");
  const month = String(date?.getMonth() + 1)?.padStart(2, "0");
  const divider = i18n.language === "nl" ? '/' : '.';
  let str = `${ day }${ divider }${ month }${ divider }${ date?.getFullYear() }`;

  if (withTime) {
    str += ` ${ hours }:${ minutes }`;
  }

  return str;
}

export function formatDate(dateStr) {
  const myDate = new Date(dateStr);
  const day = String(myDate?.getDate())?.padStart(2, '0');
  const month = String(myDate?.getMonth() + 1)?.padStart(2, '0');
  const year = myDate?.getFullYear();
  return isNaN(myDate?.getTime()) ? dateStr : `${day}/${month}/${year}`;
}

export function formatOnlyDateForGui(dateStr) {
  if (!dateStr) {
    return null;
  }

  const date = new Date(dateStr);
  return `${ String(date?.getDate()).padStart(2, "0") }/${ String(date?.getMonth() + 1)?.padStart(2, "0") }/${ date?.getFullYear() }`;
}

export function formatDateTimeForGui(dateString) {
  return dateString ? formatOnlyDateForGui(dateString) : null;
}

export function formatDateForGui(dateString) {
  if (!dateString) {
    return null;
  }

  return formatOnlyDateForGui(dateString);
}

export function transformDateForDrugs(dateStr) {
  const date = dateStr?.split("T")[0];
  return date ? date?.split("-")?.reverse()?.join(".") : dateStr;
}

export function parseDate(input) {
  if (input instanceof Date) {
    return input;
  }

  const dateRegex1 = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/; // yyyy-mm-dd or yyyy-mm-ddTHH:MM:SS
  const dateRegex2 = /^\d{2}\/\d{2}\/\d{4}(?: \d{2}:\d{2})?$/; // dd/mm/yyyy or dd/mm/yyyy HH:MM

  if (dateRegex1.test(input)) {
    return new Date(input);
  } else if (dateRegex2.test(input)) {
    const [datePart, timePart] = input?.split(" ");
    const [day, month, year] = datePart?.split("/")?.map(Number);
    const hours = timePart ? Number(timePart?.split(":")[0]) : 0;
    const minutes = timePart ? Number(timePart?.split(":")[1]) : 0;
    return new Date(year, month - 1, day, hours, minutes);
  }

  return null;
}

export function buildMeasurement(name, value, unit) {
  return {
    name,
    comment: Consts.ANY,
    dateTime: (new Date()).toString(),
    params: [{value, unit, name}],
  };
}

export const buildRisks = (dpwgDrugs, riskKind) => {
  return dpwgDrugs?.map((dpwgDrug) => {
    const substance = dpwgDrug[i18n?.language?.toUpperCase()]?.substanceName || dpwgDrug?.substanceKey;

    return {
      activeSubstance: substance,
      recommendation: JSON.stringify(dpwgDrug),
      substanceKey: dpwgDrug?.substanceKey,
      description: riskKind,
    };
  });
};

const handleAndersGenes = (
  geneName,
  genotype,
  phenotype,
  genotypeSubstances,
  phenotypeSubstances,
) => {
  const isCYP2C9 = geneName === Consts.ANDERS_GENES[0];
  const isIntermediateMetabolizer = phenotypeMap[phenotype] === Consts.INTERMEDIATE_METABOLIZER;
  const isPoorMetabolizer = phenotypeMap[phenotype] === Consts.POOR_METABOLIZER;
  let showPheno;

  if(isCYP2C9){
    showPheno = (isIntermediateMetabolizer && !Consts.NOT_CYP2C9_INTERMEDIATE_GENOS?.includes(genotype))
      || (isPoorMetabolizer && !Consts.NOT_CYP2C9_POOR_GENOS?.includes(genotype));
  } else {
    showPheno = (isIntermediateMetabolizer && !Consts.NOT_UGT1A1_GENOS?.includes(genotype))
      || (isPoorMetabolizer && !Consts.NOT_UGT1A1_GENOS?.includes(genotype));
  }

  return {
    risks: [],
    genoRisks: showPheno ? [] : buildRisks(genotypeSubstances, Consts.GENO_RISKS),
    phenoRisks: showPheno ? buildRisks(phenotypeSubstances, Consts.PHENO_RISKS) : [],
  };
};

const handleGenes = (
  genotype,
  phenotype,
  phenotypeSubstances,
  genotypeSubstances,
) => {
  let risks = [];
  let genoRisks = [];
  let phenoRisks = [];

  if (genotypeSubstances?.length > 0 && phenotypeSubstances?.length > 0) {
    risks = buildRisks(genotypeSubstances, Consts.COMPLETE_RISKS);
  } else if (phenotypeSubstances?.length > 0) {
    phenoRisks = buildRisks(phenotypeSubstances, Consts.PHENO_RISKS);
  } else if ((!phenotypeSubstances || phenotypeSubstances?.length === 0) && genotypeSubstances?.length > 0) {
    genoRisks = buildRisks(genotypeSubstances, Consts.GENO_RISKS);
  }

  return { risks, genoRisks, phenoRisks };
};

export const buildRiskObject = (
  phenotype,
  genotype,
  geneFromJson,
) => {
  const fullGenotypeRisks = geneFromJson?.genotypes?.find(geno => geno?.genotypeName === genotype);
  const genotypeSubstances = fullGenotypeRisks?.activeSubstances ? fullGenotypeRisks.activeSubstances : [];
  const alleles = genotype?.split("/");

  const genotypeAlleleRisks = alleles?.map(allele =>
    geneFromJson?.genotypes?.find(geno => geno?.genotypeName === allele)
  );

  genotypeAlleleRisks?.forEach(geno => {
    if (geno?.activeSubstances) {
      genotypeSubstances?.push(...geno?.activeSubstances);
    }
  });

  const phenotypeRisks = geneFromJson?.phenotypes?.find(pheno => pheno?.phenotypeName === phenotypeMap[phenotype]);

  if (Consts.ANDERS_GENES?.includes(geneFromJson?.geneName)) {
    return handleAndersGenes(geneFromJson?.geneName, genotype, phenotype, genotypeSubstances,
      phenotypeRisks?.activeSubstances);
  } else {
    return handleGenes(genotype, phenotype, phenotypeRisks?.activeSubstances, genotypeSubstances);
  }
};

export const buildInteractionParam = (param, withDescription) => {
  let newParam = param;

  if (param?.includes("EN")) {
    const parsed = JSON.parse(param);
    newParam = parsed[i18n.language?.toUpperCase()];

    if (withDescription) {
      newParam = newParam?.description;
    }
  }

  return newParam;
}

export const isOnlyNumber = (str) => /^\d+$/.test(str);

export const sortData = (key, items, ascending, getValue) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  return [...items]?.sort((a, b) => {
    const valueA = getValue(a);
    const valueB = getValue(b);

    if (valueA === null && valueB === null) {
      return 0;
    }

    if (valueA === null) {
      return ascending ? -1 : 1;
    }

    if (valueB === null) {
      return ascending ? 1 : -1;
    }

    const dateA = parseDate(valueA);
    const dateB = parseDate(valueB);

    if (dateA || dateB) {
      return ascending ? (dateA > dateB ? 1 : -1) : (dateA < dateB ? 1 : -1);
    }

    return ascending ? (valueA > valueB ? 1 : -1) : (valueA < valueB ? 1 : -1);
  });
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0)?.toUpperCase() + string?.slice(1);
}

export const filterIcd10Data = (data) => {
  return data?.filter(item => {
    if(item?.icd10Name[i18n.language?.toUpperCase()] !== ""){
      return item;
    }
  });
}

export const removeLastSign = (text, sign) => {
  let newText = text;

  if(newText?.endsWith(sign)){
    newText = newText?.trim()?.slice(0, -1);
  }

  return newText;
}

export function removeDdiDuplicates(array) {
  const uniqueItems = [];
  const seen = new Map();

  array?.forEach(item => {
    const key = JSON.stringify({
      ddiDrug: item?.ddiDrug?.compound,
      focusDrug: item?.focusDrug?.compound
    });

    if (seen?.has(key)) {
      const existingItem = seen?.get(key);

      if (item?.relevance?.abdataCode > existingItem?.relevance?.abdataCode) {
        seen?.set(key, item);
      }
    } else {
      seen?.set(key, item);
    }
  });

  seen?.forEach(value => uniqueItems?.push(value));
  return uniqueItems;
}

export const extractDrugStrengthInfo = (productName) => {
  const strengthExtracted = extractStrength(productName);

  const strengthUnit = strengthExtracted?.unit !== ""
    ? strengthUnits?.filter(
      (option) => option?.value?.strengthUnit === strengthExtracted?.unit,
    )?.[0]?.value
    : "";

  return [strengthExtracted?.strength, strengthUnit];
};

const extractStrength = (drugName) => {
  const regex = /(\d+[,\.]?\d*)\s*(mg|g|ml|μg|l)/i;
  const match = drugName?.match(regex);
  let strength = match ? match[1]?.replace(",", ".") : 0;
  strength = isNaN(strength) ? 0 : Number(strength);
  const unit = match ? match[2] : "";
  return {strength, unit};
};

export const checkDrugForDoubleEntry = (drugs, newProduct) => {
  const productsMatched = drugs?.filter((drug) => drug?.productName === newProduct);
  return productsMatched?.length > 0;
};

export const isSlovenia = (hospital) => {
  return hospital === "Zdravstveni center Ljubljana - VIČ"
    || hospital === "Zdravstveni center Ljubljana - BEŽIGRAD"
    || hospital === "Ambulanta KUS"
    || hospital === "ZZ Zdravje"
    || hospital === "Psychiatric Clinic BJS"
    || hospital === "University Psichiatric Clinic Ljubljana";
}

export const getPatientName = (patient, isUserStudyDirector) => {
  const id = patient?.id;
  const first = patient?.first_name?.trim();
  const last = patient?.last_name?.trim();
  const pseudonym = patient?.pseudonym?.trim();
  const login = patient?.login?.trim();

  if (isUserStudyDirector && !patient?.delete_requested) {
    return id ?? login ?? pseudonym ?? "";
  }

  if (last) {
    return first ? `${first} ${last}` : last;
  }

  return pseudonym || login || "";
};

export const toYearMonthDay = (d) => d ? new Date(d).toISOString().slice(0, 10) : "";
